import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Scissors, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AddQueueDialog, PRIORITIES } from "@/components/cutting/AddQueueDialog";
import { CutLogDialog } from "@/components/cutting/CutLogDialog";

const STATUS = { da_tagliare: "Da tagliare", in_corso: "In corso", tagliato: "Tagliato" };
const today = () => new Date().toLocaleDateString("sv-SE");
const shortDate = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const remaining = (q) => q.sizes.map((s) => ({ size: s.size, quantity: Math.max(0, s.quantity - ((q.cut_sizes || []).find((c) => c.size === s.size)?.quantity || 0)) }));

const QueueRow = ({ q, onCut }) => {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["cutting"] });
  const patch = useMutation({ mutationFn: async (p) => (await api.patch(`/cutting/queue/${q.id}`, p)).data, onSuccess: refresh });
  const del = useMutation({ mutationFn: async () => api.delete(`/cutting/queue/${q.id}`), onSuccess: () => { refresh(); toast.success("Rimosso dalla coda"); } });
  const late = q.due_date && q.due_date < today();
  return (
    <TableRow data-testid={`cut-queue-row-${q.id}`}>
      <TableCell>
        <div className="flex items-center gap-2">
          {q.image_url ? <img src={q.image_url} alt="" className="h-10 w-8 rounded object-cover" /> : <div className="h-10 w-8 rounded bg-muted" />}
          <div className="min-w-0"><p className="truncate text-sm font-medium">{q.product_name}</p><p className="text-xs text-muted-foreground">{q.color}{q.notes ? ` · ${q.notes}` : ""}</p></div>
        </div>
      </TableCell>
      <TableCell>
        <Select value={q.priority} onValueChange={(v) => patch.mutate({ priority: v })}>
          <SelectTrigger className={`h-8 w-28 ${PRIORITIES[q.priority].cls}`} data-testid={`cut-queue-priority-${q.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PRIORITIES).map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </TableCell>
      <TableCell className={`text-sm ${late ? "font-semibold text-rose-400" : "text-muted-foreground"}`}>{q.due_date ? `${shortDate(q.due_date)}${late ? " · in ritardo" : ""}` : "-"}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{q.sizes.map((s) => `${s.size}×${s.quantity}`).join("  ")}</TableCell>
      <TableCell className="text-right text-sm"><b>{num(q.cut_pieces || 0)}</b> / {num(q.pieces)}<Badge variant="outline" className="ml-2 text-[10px]">{STATUS[q.status]}</Badge></TableCell>
      <TableCell>
        <div className="flex justify-end gap-1">
          <Button size="sm" onClick={() => onCut(q)} data-testid={`cut-queue-cut-${q.id}`}><Scissors className="mr-1 h-3.5 w-3.5" />Taglia</Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => window.confirm("Rimuovere dalla coda?") && del.mutate()} data-testid={`cut-queue-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export const CuttingQueue = () => {
  const [cut, setCut] = useState(null);
  const { data: queue = [] } = useQuery({ queryKey: ["cutting", "queue"], queryFn: async () => (await api.get("/cutting/queue")).data });
  const openCut = (q) => setCut({ items: q ? [{ queue_id: q.id, product: { id: q.product_id, name: q.product_name, color: q.color, sizes: q.sizes }, qty: Object.fromEntries(remaining(q).map((s) => [s.size, String(s.quantity || "")])) }] : [] });
  return (
    <Card className="p-6" data-testid="cutting-queue">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div><h3 className="font-display text-lg font-semibold">Da tagliare</h3><p className="text-xs text-muted-foreground">Ordinati per urgenza e data di consegna.</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => openCut(null)} data-testid="cutting-new-log-btn"><Scissors className="mr-2 h-4 w-4" />Registra taglio</Button>
          <AddQueueDialog />
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Articolo</TableHead><TableHead>Urgenza</TableHead><TableHead>Consegna</TableHead><TableHead>Taglie</TableHead><TableHead className="text-right">Tagliati</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {queue.map((q) => <QueueRow key={q.id} q={q} onCut={openCut} />)}
            {queue.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground" data-testid="cutting-queue-empty">Nessun articolo da tagliare.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {cut && <CutLogDialog initial={cut} onClose={() => setCut(null)} />}
    </Card>
  );
};
